import React, { useCallback, useMemo } from "react";

import { Stack } from "@mui/material";
import { useHistory, useParams } from "react-router-dom";

import useClientDashboard from "@fartherfinance/frontend/api/Dashboard/hooks/useClientDashboard";
import {
  AdvisorId,
  ClientId,
  PortfolioPlanId,
} from "@fartherfinance/frontend/api/Types";

import { InvestmentsPath } from "@src/config/routing/RouterPaths";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import ModelPickerModal from "@src/multiCustodian/components/PortfolioPlanV2/ModelPickerModal/ModelPickerModal";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";

import PortfolioPlanTable from "./PortfolioPlanList/PortfolioPlanTable";

const PortfolioPlanList: React.FC<React.PropsWithChildren<object>> = ({
  children,
}) => {
  const history = useHistory();
  const { clientId } = useParams<{
    clientId: ClientId;
  }>();

  const clientAuth = useRequestAuth();

  const dashboard = useClientDashboard(clientId, clientAuth);
  const advisorId = useMemo(() => {
    if (dashboard.data?.advisor.advisorId) {
      return AdvisorId.parse(dashboard.data.advisor.advisorId);
    }
    return undefined;
  }, [dashboard.data]);

  const [showPicker, setShowModelPickerModal] = React.useState(false);

  const onModalClose = useCallback(() => {
    setShowModelPickerModal(false);
  }, []);

  const onModalOpen = useCallback(() => {
    setShowModelPickerModal(true);
  }, []);

  const onModelSelect = useCallback(
    (portfolioPlanId: PortfolioPlanId) => {
      history.push(
        `/Client/${clientId}/${InvestmentsPath}/PortfoliosV2/Pick/${portfolioPlanId}`,
        history.location.state
      );
    },
    [clientId, history]
  );

  return (
    <>
      <Stack
        direction={"column"}
        justifyContent={"center"}
        pt={"8px"}
        gap={"10px"}
      >
        <Stack direction={"row"} justifyContent={"flex-end"}>
          <Button
            text="Pick a Model Portfolio"
            buttonType="primary"
            variant="contained"
            onClick={onModalOpen}
          />
        </Stack>
        <PortfolioPlanTable clientId={clientId} />
      </Stack>
      {children}
      <ModelPickerModal
        onSubmit={onModelSelect}
        closeModal={onModalClose}
        isOpen={showPicker}
        clientId={clientId}
        advisorId={advisorId}
      />
    </>
  );
};

export default PortfolioPlanList;
